import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import SubscriptionInterval from "@mapmycustomers/shared/enum/SubscriptionInterval";
import CurrentPlan from "@mapmycustomers/shared/types/plans/CurrentPlan";
import Tier from "@mapmycustomers/shared/types/plans/Tier";
import User from "@mapmycustomers/shared/types/User";
import { NumberField } from "@mapmycustomers/ui";

import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import billingAnalytics from "../../billingAnalytics";
import { getLicenses, getTiers, isAnnual } from "../../store";
import { setAnnual, setLicenses } from "../../store/actions";
import formatPrice from "../../util/formatPrice";
import { isInRange } from "../../util/tier";
import { SavingsInformation } from "../../util/useSavingsInformation";
import { DescriptionColumn, PriceColumn, Row } from "../BaseRow";

import AnnualSwitch from "./AnnualSwitch";
import Description from "./Description";
import styles from "./LicenseRow.module.scss";

interface Props {
  currentPlan: CurrentPlan;
  isAnnual: boolean;
  licenses: number;
  onAnnualChange?: (isAnnual: boolean) => void;
  onLicenseChange?: (licenses: number) => void;
  savingsInformation: SavingsInformation;
  selectedTier: Tier;
  tiers: Tier[];
  users: User[];
}

const LicenseRow: React.FC<Props> = ({
  currentPlan,
  isAnnual,
  licenses,
  onAnnualChange,
  onLicenseChange,
  savingsInformation,
  selectedTier,
  tiers,
  users,
}) => {
  const intl = useIntl();
  const numberOfAvailableLicenseIntervals = tiers.filter(
    (tier) => isInRange(tier.licenseLimit, licenses) && !tier.isTrial
  ).length;

  const hasMonthlyEquivalent =
    numberOfAvailableLicenseIntervals > 1 &&
    selectedTier.interval === SubscriptionInterval.ANNUAL &&
    tiers.some(
      (tier) =>
        isInRange(tier.licenseLimit, licenses) &&
        !tier.isTrial &&
        tier.interval === SubscriptionInterval.MONTHLY
    );

  // For trial users they're only allowed to start from number of users in their org
  const minimumAllowedLicensesToAdd = currentPlan.tier.isTrial ? users.length : 0;

  const [addedLicenses, setAddedLicenses] = useState<number>(licenses - currentPlan.licenses);

  const [error, setError] = useState<string | undefined>();
  const handleChangeLicenses = useCallback(
    (value: number | undefined) => {
      if (value !== undefined && value >= 0) {
        if (onLicenseChange) {
          onLicenseChange(currentPlan.licenses + value);
          billingAnalytics.clicked(["Change License Count"], {
            count: currentPlan.licenses + value,
          });
          setError(undefined);
        }
      } else if (value === undefined) {
        setError(
          intl.formatMessage({
            id: "billing.licenses.error.nan",
            defaultMessage: "Please enter a number",
            description: "Non-a-number error in licenses field on Billing page",
          })
        );
      } else if (value < minimumAllowedLicensesToAdd) {
        // this should not really happen, but still keeping this code for weird conditions
        setError(
          intl.formatMessage({
            id: "billing.licenses.error.negative",
            defaultMessage: "You can not reduce a number of licenses",
            description: "Negative number error in licenses field on Billing page",
          })
        );
      }
      setAddedLicenses(value ?? 0);
    },
    [currentPlan.licenses, intl, minimumAllowedLicensesToAdd, onLicenseChange, setAddedLicenses]
  );

  const handleAnnualChange = useCallback(
    (enabled: boolean) => {
      onAnnualChange?.(enabled);
      billingAnalytics.clicked(["Toggle Annual Switch", enabled ? "On" : "Off"]);
    },
    [onAnnualChange]
  );

  return (
    <Row>
      <DescriptionColumn
        bigTitle
        className={styles.descriptionColumn}
        description={<Description />}
        title={intl.formatMessage({
          id: "billing.licenses.title",
          defaultMessage: "Add Licenses",
          description: "Title of the Licenses item card on Billing",
        })}
      />
      <PriceColumn className={styles.priceColumn}>
        <NumberField
          className={styles.licensesField}
          error={error}
          min={minimumAllowedLicensesToAdd}
          onChange={handleChangeLicenses}
          step={1}
          value={addedLicenses}
        />
        <AnnualSwitch
          disabled={
            numberOfAvailableLicenseIntervals < 2 ||
            currentPlan.tier.interval === SubscriptionInterval.ANNUAL
          }
          isAnnual={isAnnual}
          onChange={handleAnnualChange}
          savingsInformation={savingsInformation}
        />
        <div className={styles.price}>
          {intl.formatMessage(
            {
              id: "billing.licenses.price",
              defaultMessage:
                "{price}{interval, select, annual { / year} monthly { / month} other {}}",
              description: "Price for selected licenses and plan on Billing page",
            },
            {
              interval: hasMonthlyEquivalent ? SubscriptionInterval.MONTHLY : selectedTier.interval,
              price: formatPrice(intl, {
                ...selectedTier.price,
                value: hasMonthlyEquivalent
                  ? selectedTier.price.value / 12
                  : selectedTier.price.value,
              }),
            }
          )}
        </div>
      </PriceColumn>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  isAnnual: isAnnual(state),
  licenses: getLicenses(state),
  tiers: getTiers(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onAnnualChange: setAnnual,
  onLicenseChange: setLicenses,
};

export default connect(mapStateToProps, mapDispatchToProps)(LicenseRow);
